import {AxiosRequestHeaders} from 'axios';
import {requestFormDataPost, requestGet, requestPost,} from '@/services/request';
import {ExamMethod, FcrpdPaperStatus, TCheckTimu, TPreviewTimu, TTimu, TTimuType,} from '@/types';
import {TExamPlanItem} from '@/page-components/detail/Intro/exam_plan';
import {TExamBasicInfo} from '@/types/exam';
import {TApi} from '@/types/api';
import {requestCasGet} from '@/services/casRequest';

// 获取替他人报名的订单的支付情况
export function apiGetOtherPeoplePay(params: { certId: string }): Promise<
  TApi<{
    isOrder: boolean;
    payState: boolean;
  }>
> {
  return requestGet('/v1/cert/signup/batch/pay/state', params);
}

// 获取用户报考信息
export function apiExamInfo(cert_id: string): Promise<
  TApi<
    | TExamPlanItem[]
    | {
    isHistoryPass?: boolean; // 是否是历史通过数据，有这一项并为true，不显示报名按钮
  }
  >
> {
  return requestGet('/v1/cert/user/entry/info', {certId: cert_id});
}

// EJC 企业学习项目成员访问权限（登录后调用）
export function apiEjcAccess(certId: string): Promise<
  TApi<{
    restricted: boolean; // 该认证是否绑定了项目成员限制
    hasAccess: boolean; // 当前用户是否有访问权限
  }>
> {
  return requestGet('/v1/cert/ejc/access', {certId});
}

// 获取考试批次
export function apiExamBatch() {
  return requestGet('/v1/cert/exam/batch', {});
}

//获取某场考试考题
export function apiGetExamQuestion(params: { paperId: any; type: 'A' | 'P' }) {
  return requestGet('/v1/exam/questions', params);
}

//获取 a系列某场考试考题
export function apiGetExamQuestionA(
  params: { paperId: any },
  extraHeader?: AxiosRequestHeaders,
): Promise<
  TApi<{
    examExpiresTime: number | null;
    saveTime: number | null;
    list: TTimu[];
    state: FcrpdPaperStatus;
    score: null | number;
    commitment: boolean;
    faceVerify: boolean;
    entryId: string;
  }>
> {
  return requestGet('/v1/exam/questions/a', params, extraHeader);
}

// 获取p系列考试考题
export function apiGetExamQuestionP(
  params: { paperId: any },
  extraHeader?: AxiosRequestHeaders,
): Promise<
  TApi<{
    examExpiresTime: number | null;
    saveTime: number | null;
    list: TTimu[];
    state: FcrpdPaperStatus;
    score: null | number;
    commitment: boolean;
    faceVerify: boolean;
    entryId: string;
  }>
> {
  return requestGet('/v1/exam/questions/p', params, extraHeader);
}

// 获取某场考试基本信息
export function apiGetExamBasicInfo(params: {
  examId: any;
}): Promise<TApi<TExamBasicInfo[]>> {
  return requestGet('/v1/exam/basic/info', params);
}

// 保存答题记录
export function apiSaveExamRecord(
  params: any,
): Promise<{ code: number; success: boolean; msg: string }> {
  return requestPost('/v1/exam/questions/save', params);
}

// 交卷
export function apiSubmitExamRecord(
  params: {
    paperId: any;
    certType: string;
    list: {
      timuId: string;
      type: TTimuType;
      id: string;
      answer: string;
    }[];
  }[],
): Promise<
  TApi<{
    result: 'pass' | 'watting' | 'fail' | 'waiting';
    score: null | number;
  }>
> {
  return requestPost('/v1/exam/paper/submit', params);
}

// 获取答题记录详情
export function apiGetPreviewDetail(paperId: any): Promise<
  TApi<{
    tempScore: number;
    publishTime: null | number;
    submitTime: null | number;
    certSchName: null | number;
    teacherUid: null | string; // 阅卷老师的uid
    paperUid: string; // 考生的uid
    paperRealname: string; // 考生的真实姓名
    paperUsername: string; // 考生的用户名
    saveTime: null | number;
    comName: string | null; // 考生的企业名称
    cheatTip: string | null; // 作弊提示文案
    score: number | null; // 总分
    list: TCheckTimu[];
  }>
> {
  return requestGet('/v1/exam/view/scores', {paperId});
}

// 上传附件
export function apiUploadAttach(params: { file: any }): Promise<
  TApi<{
    name: string;
    path: string;
    size: string;
  }>
> {
  return requestFormDataPost('/v1/exam/upload/attachments', params);
}

// 获取某老师阅卷表格
export function apiGetTeacherPreview(params: {
  page?: number;
  pagesize?: number;
  type: '0' | '1';
}): Promise<
  TApi<{
    publishTime: null | number;
    submitTime: null | number;
    certSchName: null | number;
    list: TPreviewTimu[];
  }>
> {
  return requestGet('/v1/exam/view/scores', params);
}

// 老师提交阅卷结果
export function apiTeacherSubmitRecord(
  params: {
    paperId: any;
    list: {
      id: string;
      score: number;
      comment: string;
    }[];
  }[],
): Promise<{
  code: number;
  success: boolean;
  msg?: string;
}> {
  return requestPost('/v1/exam/teacher/questions/submit', params);
}

// 老师保存阅卷结果
export function apiTeacherSaveRecord(
  params: {
    paperId: any;
    list: {
      id: string;
      score: number;
      comment: string;
    }[];
  }[],
): Promise<{
  code: number;
  success: boolean;
  msg?: string;
}> {
  return requestPost('/v1/exam/teacher/questions/save', params);
}

// p系列 获取报考信息
export function apiGetPExamInfo(certId: string): Promise<
  TApi<{
    list: {
      price: number;
      stuPrice: null | number;
      supplierPrice: null | number;
      examid: string;
      duration: number;
      examName: string;
    }[];
    method: ExamMethod;
    certTypeName: string;
    isPass: boolean;
    premise: string | null;
  }>
> {
  return requestCasGet('/v1/cert/exam/enter/info', {certId});
}

// 签署承诺书
export function updateCommitment(paperId: string) {
  return requestGet(`/v1/cert/exam/commitment?paperId=${paperId}`, {});
}

// 获取签署承诺书结果
export function getCommitment(paperId: string) {
  return requestGet(`/v1/cert/get/commitment?paperId=${paperId}`, {});
}

export function getEntry(entryId: string) {
  return requestGet(`/v1/cert/get/entry?entryId=${entryId}`, {});
}

export function updateFaceVerify(paperId: string) {
  return requestGet(`/v1/cert/update/face/verify?paperId=${paperId}`, {});
}

export function examAccessLog(params: any) {
  return requestPost('/v1/cert/exam/access/log', params);
}

export function refreshFaceVerify(params: any) {
  return requestPost('/v1/cert/face/verify/refresh', params);
}
